import React, { useState } from "react";
import {
  StyleProp,
  StyleSheet,
  TextInput,
  TextInputProps,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";

type PasswordInputProps = Omit<TextInputProps, "secureTextEntry" | "style"> & {
  style?: StyleProp<TextStyle>;
  iconColor?: string;
};

/**
 * Password field with a show/hide ("eye") toggle. Forwards every TextInput prop
 * (value, onChangeText, placeholder, returnKeyType, testID, ...) through to the
 * underlying input so it behaves identically to the plain inputs it replaces.
 *
 * Spacing/layout styles from `style` (margins, alignSelf, width) are hoisted to
 * the wrapper so the field keeps the same position in its parent; the rest
 * (border, height, padding, font) stays on the input itself.
 */
export default function PasswordInput({
  style,
  iconColor = "#8B9099",
  testID,
  ...textInputProps
}: PasswordInputProps) {
  const [isVisible, setIsVisible] = useState(false);

  const flattened = (StyleSheet.flatten(style) ?? {}) as TextStyle;
  const {
    margin,
    marginTop,
    marginBottom,
    marginVertical,
    marginHorizontal,
    marginLeft,
    marginRight,
    alignSelf,
    width,
    ...inputStyle
  } = flattened;

  const wrapperStyle: ViewStyle = {
    margin,
    marginTop,
    marginBottom,
    marginVertical,
    marginHorizontal,
    marginLeft,
    marginRight,
    alignSelf,
    width: width ?? "100%",
  };

  return (
    <View style={[wrapperStyle, styles.wrapper]}>
      <TextInput
        autoCapitalize="none"
        autoCorrect={false}
        {...textInputProps}
        testID={testID}
        style={[inputStyle, styles.input]}
        secureTextEntry={!isVisible}
      />
      <TouchableOpacity
        onPress={() => setIsVisible((prev) => !prev)}
        style={styles.toggle}
        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        accessibilityRole="button"
        accessibilityLabel={isVisible ? "Hide password" : "Show password"}
        testID={testID ? `${testID}-toggle` : undefined}
      >
        <Ionicons
          name={isVisible ? "eye-off-outline" : "eye-outline"}
          size={22}
          color={iconColor}
        />
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    position: "relative",
    justifyContent: "center",
  },
  input: {
    width: "100%",
    paddingRight: 48,
  },
  toggle: {
    position: "absolute",
    right: 10,
    top: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 6,
  },
});
